import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { InlineLoadingIndicator } from '@/components/ui/inline-loading';
import { semanticTokens } from '@packages/config/theme';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

interface WalletOnboardingStateProps {
  kycStatus: string | null;
  canCreateAddress: boolean;
  isCreatingWallet?: boolean;
  onCreateWallet?: () => void;
}

export function WalletOnboardingState({ 
  kycStatus, 
  canCreateAddress,
  isCreatingWallet = false,
  onCreateWallet 
}: WalletOnboardingStateProps) {
  const navigate = useNavigate();

  // KYC not approved
  if (!canCreateAddress) {
    const isPending = kycStatus === 'under_review' || kycStatus === 'awaiting_questionnaire' || kycStatus === 'awaiting_ubo';
    const isRejected = kycStatus === 'rejected';
    
    return (
      <Card className={cn(semanticTokens.motion.standard, "border-2")}>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className={cn(
              "w-12 h-12 rounded-full flex items-center justify-center",
              isPending ? semanticTokens.warning.bg : semanticTokens.danger.bg
            )}>
              {isPending ? (
                <InlineLoadingIndicator size="md" className={semanticTokens.warning.accent} aria-hidden="true" />
              ) : (
                <SFIcon icon={sfIcons.warning} className={cn("w-6 h-6", semanticTokens.danger.accent)} aria-hidden="true" />
              )}
            </div>
            <div>
              <CardTitle className={semanticTokens.typography.h3}>
                {isPending ? 'Verification in Progress' : isRejected ? 'Verification Required' : 'Complete Verification'}
              </CardTitle>
              <CardDescription className={cn(semanticTokens.typography.body, "mt-1")}>
                {isPending 
                  ? 'Your identity verification is being reviewed. This usually takes a few minutes.'
                  : isRejected
                  ? 'Your verification was not approved. Please review the requirements and try again.'
                  : 'Complete identity verification to create your wallet and start using Supabank.'
                }
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className={cn("space-y-4", semanticTokens.spacing.md.replace('p-', 'space-y-'))}>
          {isPending ? (
            <Alert className={cn(semanticTokens.info.bg, semanticTokens.info.border)}>
              <SFIcon icon={sfIcons.info} className={cn("w-4 h-4", semanticTokens.info.accent)} aria-hidden="true" />
              <AlertDescription className={cn(semanticTokens.typography.body, semanticTokens.info.fg)}>
                We'll notify you once your verification is complete. You can continue to your profile to check status.
              </AlertDescription>
            </Alert>
          ) : (
            <Button
              onClick={() => navigate('/dashboard/profile')}
              className={cn("w-full min-h-[44px]", semanticTokens.motion.standard, semanticTokens.focus.ring)}
              aria-label="Go to profile to complete verification"
            >
              {isRejected ? 'Review Verification' : 'Complete Verification'}
              <SFIcon icon={sfIcons.next} className="w-4 h-4 ml-2" aria-hidden="true" />
            </Button>
          )}
          {isPending && (
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard/profile')}
              className={cn("w-full min-h-[44px]", semanticTokens.motion.standard, semanticTokens.focus.ring)}
              aria-label="View profile status"
            >
              View Status
              <SFIcon icon={sfIcons.next} className="w-4 h-4 ml-2" aria-hidden="true" />
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  // KYC approved but no wallet - show creation CTA
  return (
    <Card className={cn(semanticTokens.motion.standard, "border-2 border-dashed")}>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className={cn(
            "w-12 h-12 rounded-full flex items-center justify-center",
            semanticTokens.info.bg
          )}>
            <SFIcon icon={sfIcons.wallet} className={cn("w-6 h-6", semanticTokens.info.accent)} aria-hidden="true" />
          </div>
          <div>
            <CardTitle className={semanticTokens.typography.h3}>Create Your First Wallet</CardTitle>
            <CardDescription className={cn(semanticTokens.typography.body, "mt-1")}>
              Your wallet will be created automatically when you add funds, or you can create it now.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className={cn("space-y-4", semanticTokens.spacing.md.replace('p-', 'space-y-'))}>
        <div className={cn("space-y-2", semanticTokens.spacing.sm.replace('p-', 'space-y-'))}>
          <div className={cn("flex items-center gap-2", semanticTokens.spacing.sm.replace('p-', 'gap-'))}>
            <SFIcon icon={sfIcons.success} className={cn("w-4 h-4", semanticTokens.positive.accent)} aria-hidden="true" />
            <span className={cn(semanticTokens.typography.body)}>Identity verified</span>
          </div>
          <div className={cn("flex items-center gap-2", semanticTokens.spacing.sm.replace('p-', 'gap-'))}>
            <SFIcon icon={sfIcons.wallet} className={cn("w-4 h-4", semanticTokens.fg.muted)} aria-hidden="true" />
            <span className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>Wallet ready to create</span>
          </div>
        </div>
        
        {onCreateWallet && (
          <Button
            onClick={onCreateWallet}
            disabled={isCreatingWallet}
            className={cn("w-full min-h-[44px]", semanticTokens.motion.standard, semanticTokens.focus.ring)}
            aria-label="Create wallet now"
          >
            {isCreatingWallet ? (
              <>
                <InlineLoadingIndicator size="sm" className="mr-2" aria-hidden="true" />
                Creating Wallet...
              </>
            ) : (
              <>
                <SFIcon icon={sfIcons.wallet} className="w-4 h-4 mr-2" aria-hidden="true" />
                Create Wallet Now
              </>
            )}
          </Button>
        )}
        
        <p className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted, "text-center")}>
          Or click "Add Funds" above to create your wallet automatically
        </p>
      </CardContent>
    </Card>
  );
}

